import React, { useMemo, useState } from 'react'
import { FiChevronUp, FiChevronDown, FiExternalLink } from 'react-icons/fi'
import { useTheme } from 'styled-components'

import { SearchField } from '../..'
import { Button } from '../../../../../common/components'
import { ClassStatus } from '../../../hooks/useReportsBySchool'

import { Container, Header, Label, Table } from '../styles'

interface ReportProps {
  data: ClassStatus[]
}

export const ClassStatusReport: React.FC<ReportProps> = ({ data }) => {
  const theme = useTheme()

  const [search, setSearch] = useState<string>('')
  const [column, setColumn] = useState<keyof ClassStatus | null>(null)
  const [inverse, setInverse] = useState<boolean>(false)

  const handleExport = () => {
    const html = document.getElementById('excel-export--class-status')
    const table = html?.outerHTML
    window.open(
      'data:application/vnd.ms-excel,' + encodeURIComponent(table as string)
    )
  }

  const handleSort = (value: keyof ClassStatus) => {
    if (value === column) {
      setInverse(current => !current)
    } else {
      setColumn(value)
      setInverse(false)
    }
  }

  const filtered = useMemo(() => {
    if (!search) return data
    const arr =
      data.filter(
        item =>
          item.class.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !==
          -1
      ) || []
    return arr
  }, [search, data])

  const sorted = useMemo(() => {
    if (!column) return filtered
    const arr = filtered.sort((a, b) => {
      if (a[column] < b[column]) {
        if (inverse) {
          return 1
        }
        return -1
      }

      if (a[column] > b[column]) {
        if (inverse) {
          return -1
        }
        return 1
      }

      return 0
    })
    return arr
  }, [column, inverse, search, data])

  const totalAccessNumber = useMemo(() => {
    if (!filtered) return 0
    return filtered.reduce((sum, item) => sum + item.accessNumber, 0)
  }, [data, search, filtered])

  const totalBooksNumber = useMemo(() => {
    if (!filtered) return 0
    return filtered.reduce((sum, item) => sum + item.booksNumber, 0)
  }, [data, search, filtered])

  const totalCompletedBooks = useMemo(() => {
    if (!filtered) return 0
    return filtered.reduce((sum, item) => sum + item.completedBooks, 0)
  }, [data, search, filtered])

  return (
    <Container>
      <Header>
        <SearchField
          placeholder="Filtrar por aluno"
          value={search}
          onChange={setSearch}
        />
        <Button variant="primary" onClick={handleExport}>
          <FiExternalLink
            color={theme.palette.buttons.primary.default.label}
            size={20}
          />
          Exportar
        </Button>
      </Header>
      <Label>
        Clique no cabeçalho da coluna desejada para ordenar a tabela
      </Label>
      <Table>
        <table id="excel-export--class-status">
          <thead>
            <tr>
              <th onClick={() => handleSort('class')}>
                <span>
                  Turmas{' '}
                  {column === 'class' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
              <th onClick={() => handleSort('accessNumber')}>
                <span>
                  Quantidade de acessos{' '}
                  {column === 'accessNumber' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
              <th onClick={() => handleSort('booksNumber')}>
                <span>
                  Quantidade de livros acessados{' '}
                  {column === 'booksNumber' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
              <th onClick={() => handleSort('completedBooks')}>
                <span>
                  Quantidade de livros finalizados{' '}
                  {column === 'completedBooks' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
              <th onClick={() => handleSort('timeInApp')}>
                <span>
                  Tempo na ferramenta{' '}
                  {column === 'timeInApp' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted?.map((item, index) => (
              <tr key={index}>
                <td>
                  <span>{item.class}</span>
                </td>
                <td>
                  <span>{item.accessNumber}</span>
                </td>
                <td>
                  <span>{item.booksNumber}</span>
                </td>
                <td>
                  <span>{item.completedBooks}</span>
                </td>

                <td>
                  <span>{item.timeInApp}</span>
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>
                <span>Total geral</span>
              </td>
              <td>
                <span>{totalAccessNumber}</span>
              </td>
              <td>
                <span>{totalBooksNumber}</span>
              </td>
              <td>
                <span>{totalCompletedBooks}</span>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Table>
    </Container>
  )
}
